import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Plus, Trash2, Loader2, AlertCircle, X, ChevronDown } from 'lucide-react';
import { employeeService } from '../services/employeeService';
import { Badge } from '../../../../components/ui/Badge';

const LEVEL_CONFIG = {
  básico:      { label: 'Básico',      className: 'bg-gray-700 text-gray-200' },
  intermedio:  { label: 'Intermedio',  className: 'bg-blue-900/60 text-blue-200' },
  avanzado:    { label: 'Avanzado',    className: 'bg-emerald-900/60 text-emerald-200' },
  experto:     { label: 'Experto',     className: 'bg-violet-900/60 text-violet-200' },
};

const LEVELS = Object.keys(LEVEL_CONFIG);

function isExpiringSoon(expiresAt) {
  if (!expiresAt) return false;
  const diff = new Date(expiresAt) - new Date();
  return diff > 0 && diff < 60 * 24 * 60 * 60 * 1000; // < 60 days
}

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

function LevelBadge({ level }) {
  const cfg = LEVEL_CONFIG[level?.toLowerCase()] ?? { label: level, className: 'bg-gray-700 text-gray-200' };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

function AssignSkillModal({ employeeId, onClose, onAssigned, onCreateSkill }) {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [form, setForm] = useState({
    skillId: '',
    level: 'básico',
    acquiredAt: new Date().toISOString().slice(0, 10),
    expiresAt: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    employeeService.getSkillsCatalog()
      .then(setCatalog)
      .catch(() => setCatalog([]))
      .finally(() => setLoadingCatalog(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.skillId) { setError('Selecciona una habilidad.'); return; }
    setSaving(true);
    setError(null);
    try {
      await employeeService.assignSkillToEmployee(employeeId, {
        skillId: Number(form.skillId),
        level: form.level,
        acquiredAt: form.acquiredAt ? new Date(form.acquiredAt).toISOString() : new Date().toISOString(),
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      });
      onAssigned();
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al asignar la habilidad.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Añadir Habilidad</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Skill selector */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Habilidad *</label>
            {loadingCatalog ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando catálogo…
              </div>
            ) : catalog.length === 0 ? (
              <div className="rounded-lg border border-gray-600 bg-gray-700/40 px-3 py-3 text-sm text-gray-400">
                <p className="mb-2">El catálogo está vacío.</p>
                {onCreateSkill && (
                  <button
                    type="button"
                    onClick={() => { onClose(); onCreateSkill(); }}
                    className="inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Crear habilidad en el catálogo
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <select
                  value={form.skillId}
                  onChange={(e) => setForm((f) => ({ ...f, skillId: e.target.value }))}
                  required
                  className="w-full appearance-none bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm pr-8 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                >
                  <option value="">Seleccionar habilidad…</option>
                  {catalog.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.category ? ` — ${s.category}` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            )}
          </div>

          {/* Level */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Nivel *</label>
            <div className="relative">
              <select
                value={form.level}
                onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                required
                className="w-full appearance-none bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm pr-8 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>{LEVEL_CONFIG[l].label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Acquired at */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Fecha de adquisición *</label>
            <input
              type="date"
              value={form.acquiredAt}
              onChange={(e) => setForm((f) => ({ ...f, acquiredAt: e.target.value }))}
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            />
          </div>

          {/* Expires at */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Fecha de caducidad <span className="text-gray-500">(opcional)</span></label>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || loadingCatalog || catalog.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? 'Guardando…' : 'Asignar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function EmployeeSkillsSection({ selectedEmployee, onSkillsChanged, onCreateSkill, embedded = false }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const loadSkills = useCallback(async (empId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeService.getEmployeeSkills(empId);
      setSkills(data);
    } catch (err) {
      setError(err?.message || 'Error al cargar habilidades.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEmployee?.id) {
      loadSkills(selectedEmployee.id);
    } else {
      setSkills([]);
    }
  }, [selectedEmployee?.id, loadSkills]);

  const handleRevoke = async (assignId) => {
    if (!window.confirm('¿Eliminar esta habilidad del empleado?')) return;
    setRevoking(assignId);
    try {
      await employeeService.revokeEmployeeSkill(assignId);
      setSkills((prev) => prev.filter((s) => s.id !== assignId));
      onSkillsChanged?.();
    } catch (err) {
      alert(err?.message || 'Error al revocar la habilidad.');
    } finally {
      setRevoking(null);
    }
  };

  const handleAssigned = () => {
    if (selectedEmployee?.id) loadSkills(selectedEmployee.id);
    onSkillsChanged?.();
  };

  if (!selectedEmployee) return null;

  const fullName = [selectedEmployee.first_name, selectedEmployee.last_name].filter(Boolean).join(' ') || 'Empleado';

  const addButton = (
    <button
      type="button"
      onClick={() => setShowModal(true)}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium transition-colors shrink-0"
    >
      <Plus className="w-4 h-4" />
      Añadir habilidad
    </button>
  );

  const skillList = (
    <>
        {loading && (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando habilidades…
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {!loading && !error && skills.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Zap className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin habilidades registradas</p>
          </div>
        )}

        {!loading && !error && skills.length > 0 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${embedded ? '' : 'xl:grid-cols-3'}`}>
            {skills.map((es) => {
              const expired = isExpired(es.expires_at);
              const expiring = isExpiringSoon(es.expires_at);
              return (
                <div
                  key={es.id}
                  className={`relative rounded-xl border p-4 flex flex-col gap-2 ${
                    expired
                      ? 'border-red-800/60 bg-red-900/10'
                      : expiring
                      ? 'border-orange-700/60 bg-orange-900/10'
                      : 'border-gray-700 bg-gray-700/40'
                  }`}
                >
                  <div className="pr-6">
                    <p className="font-medium text-white text-sm leading-tight">
                      {es.skill?.name ?? `Skill #${es.skill_id}`}
                    </p>
                    {es.skill?.category && (
                      <p className="text-xs text-gray-400 mt-0.5">{es.skill.category}</p>
                    )}
                  </div>

                  <LevelBadge level={es.level} />

                  <div className="text-xs text-gray-400 space-y-0.5">
                    {es.acquired_at && (
                      <p>Adquirida: <span className="text-gray-300">{new Date(es.acquired_at).toLocaleDateString('es-ES')}</span></p>
                    )}
                    {es.expires_at && (
                      <p className={expired ? 'text-red-400' : expiring ? 'text-orange-400' : ''}>
                        {expired ? 'Caducada: ' : expiring ? 'Caduca pronto: ' : 'Caduca: '}
                        <span>{new Date(es.expires_at).toLocaleDateString('es-ES')}</span>
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRevoke(es.id)}
                    disabled={revoking === es.id}
                    className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-40"
                    title="Eliminar habilidad"
                  >
                    {revoking === es.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />
                    }
                  </button>
                </div>
              );
            })}
          </div>
        )}
    </>
  );

  const assignModal = showModal && (
    <AssignSkillModal
      employeeId={selectedEmployee.id}
      onClose={() => setShowModal(false)}
      onAssigned={handleAssigned}
      onCreateSkill={onCreateSkill}
    />
  );

  if (embedded) {
    return (
      <section>
        <div className="flex items-center justify-between gap-4 mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Habilidades
          </h3>
          {addButton}
        </div>
        {skillList}
        {assignModal}
      </section>
    );
  }

  return (
    <>
      <div className="mt-8 mb-6">
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-yellow-400 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-white">Habilidades del Empleado</h2>
                  <p className="mt-1 text-sm text-gray-400">Empleado seleccionado: {fullName}</p>
                </div>
              </div>
              {addButton}
            </div>
          </div>

          <div className="p-4 sm:p-6 w-full min-w-0">
            {skillList}
          </div>
        </div>
      </div>
      {assignModal}
    </>
  );
}
